import { FLIGHT_LOCAL, type ExportModel, type MoneyRange } from "./export-model";

/** Characters a spreadsheet reads as the start of a formula. */
const FORMULA_START = /^[=+\-@\t\r]/;

/**
 * One CSV cell, safe to open in a spreadsheet: a text cell that starts like a formula (`=`, `+`,
 * `-`, `@`, tab or carriage return) gets a leading `'` so Excel and Sheets show it as text
 * instead of running it, and cells with quotes, commas or line breaks are quoted. Numbers are left
 * alone, so negative amounts stay numbers.
 * @param value - A text or numeric cell.
 * @returns The cell as written in the file.
 * @throws Never.
 */
export const csvCell = (value: string | number): string => {
  if (typeof value === "number") return String(value);
  const text = FORMULA_START.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Rows as CSV text, every cell through {@link csvCell}.
 * @param rows - The rows.
 * @returns The CSV text.
 * @throws Never.
 */
export const toCsv = (rows: ReadonlyArray<ReadonlyArray<string | number>>): string =>
  rows.map((row) => row.map(csvCell).join(",")).join("\n");

/**
 * The estimate as a long CSV, one row per city and cost, the same shape as the workbook's Costs
 * sheet so it filters and pivots: each cost per person and for the whole group, then the city's
 * "All costs" row. Every amount column names its currency and the group size, the cost names its
 * nights, and the basis says what the amount covers. Amounts stay numbers; a cost with no data
 * leaves its amounts empty and says why in the notes.
 * @param model - The export model.
 * @returns CSV text.
 * @throws Never.
 */
export const estimateCsv = (model: ExportModel): string => {
  const { currency, trip } = model;
  const group = trip.headcount > 0 ? `${trip.headcount} ${trip.headcount === 1 ? "person" : "people"}` : "the group";
  const amounts = (range: MoneyRange | null): Array<number | string> => (range ? [range.low, range.high] : ["", ""]);
  const header = [
    "City",
    "Cost",
    "Basis",
    `Per person low (${currency})`,
    `Per person high (${currency})`,
    `Total for ${group} low (${currency})`,
    `Total for ${group} high (${currency})`,
    "Source",
    "Notes",
  ];
  const rows = model.cities.flatMap((city) => {
    const unpriced = model.flights.filter(
      (flight) => flight.city === city.city && flight.farePerPerson === null && flight.status !== FLIGHT_LOCAL
    );
    const flightNotes = [
      ...unpriced.map((flight) => `No fare from ${flight.from} (${flight.people}): ${flight.status}`),
      trip.withoutHomeCity > 0 ? `Leaves out flights for ${trip.withoutHomeCity} without a home city` : null,
    ];
    const costRows = model.costs
      .filter((cost) => cost.city === city.city)
      .map((cost) => [
        cost.city,
        cost.item,
        cost.basis,
        ...amounts(cost.perPerson),
        ...amounts(cost.total),
        cost.source,
        (cost.item === "Flights" ? flightNotes : []).filter(Boolean).join(". "),
      ]);
    const allCosts = [
      city.city,
      "All costs",
      city.cheapest ? "Cheapest city" : "",
      ...amounts(city.perPerson),
      ...amounts(city.total),
      "",
      city.excludes.length ? `Excludes ${city.excludes.join(" and ")}` : "",
    ];
    return [...costRows, allCosts];
  });
  return toCsv([header, ...rows]);
};
